import { useEffect, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import styles from "../WorkoutPlanDialog.module.scss";

const PLACEHOLDER = "Тренировъчна програма";
const MAX_LENGTH = 100;

interface IWorkoutPlanNameFieldProps {
  value: string | null;
  isReadOnly: boolean;
  onCommit: (value: string) => Promise<void> | void;
}

/**
 * Click-to-edit header field for the workout plan name.
 *
 * When idle it looks like the static title used previously. Clicking turns it
 * into an inline input; blur or Enter commits, Escape discards. We hold the
 * draft in local state so a slow network round-trip doesn't snap the caret or
 * stutter the displayed text mid-edit. Read-only mode keeps the rendering but
 * never enters edit state.
 */
export const WorkoutPlanNameField = ({
  value,
  isReadOnly,
  onCommit,
}: IWorkoutPlanNameFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep the local draft in sync with server-side changes while the field is
  // idle. If the user is actively typing we leave their draft alone — clobbering
  // mid-edit would feel like the UI is fighting them.
  useEffect(() => {
    if (!isEditing) setDraft(value ?? "");
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commit = () => {
    setIsEditing(false);
    const next = draft.trim();
    const previous = (value ?? "").trim();
    if (next === previous) return;
    void onCommit(next);
  };

  const cancel = () => {
    setIsEditing(false);
    setDraft(value ?? "");
  };

  if (isReadOnly || !isEditing) {
    const trimmed = (value ?? "").trim();
    const hasName = trimmed.length > 0;
    return (
      <button
        type="button"
        className={classNames(styles.name_button, {
          [styles.name_button_placeholder]: !hasName,
          [styles.name_button_readonly]: isReadOnly,
        })}
        disabled={isReadOnly}
        onClick={() => {
          if (isReadOnly) return;
          setIsEditing(true);
        }}
        aria-label={hasName ? `Преименувай: ${trimmed}` : "Дай име на тренировката"}
      >
        {hasName ? trimmed : PLACEHOLDER}
        {!isReadOnly && (
          <span className={styles.name_button_hint} aria-hidden>
            Промени
          </span>
        )}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      className={styles.name_input}
      value={draft}
      placeholder={PLACEHOLDER}
      maxLength={MAX_LENGTH}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        } else if (event.key === "Escape") {
          event.preventDefault();
          cancel();
        }
      }}
    />
  );
};
